import { Component, ViewChild, OnInit } from '@angular/core'
import { Router, ActivatedRoute } from '@angular/router'
import { IPayer, PayerODataService, PayerStateODataService } from '@exchange/domain/.'
import { ColDef } from 'ag-grid-community'
import { ToastrService } from 'ngx-toastr'
import {
  ActionButtonsColumnRendererComponent,
  ConfirmModalComponent,
  GridWrapperComponent,
  LookupFilterComponent,
  Option
} from 'common-components'
import { BsModalService } from 'ngx-bootstrap/modal'
import { DateTimeColumnRendererComponent } from '@exchange/app/ui/shared/controls/grid-renderer/date-time-column.renderer.component'

@Component({
  selector: 'app-payers-list',
  templateUrl: './payers-list.component.html',
  standalone: false
})
export class PayersListComponent implements OnInit {
  @ViewChild('grid', { static: true }) grid: GridWrapperComponent

  model: IPayer = null
  states: Option[] = []

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private odata: PayerODataService,
    private stateService: PayerStateODataService,
    private toastr: ToastrService,
    private modalService: BsModalService
  ) {}

  ngOnInit() {
    this.stateService
      .query()
      .orderBy('Name')
      .exec()
      .subscribe(response => {
        response.data.forEach(x =>
          this.states.push(new Option(x.Id, `${x.Abbreviation} - (${x.Name})`, x))
        )

        this.grid.setLookupFilterOptions('PhysicalAddress.StateId', this.states)
      })

    this.initializeGrid()

    const sm = this.route.snapshot.queryParams['successMessage']
    if (sm) {
      this.toastr.success(sm)
    }
  }

  initializeGrid() {
    this.grid.gridOptions.context = {
      canEdit: true,
      canDelete: true,
      editHandler: this.editClicked.bind(this),
      deleteHandler: this.deleteClicked.bind(this),
      overrideTimeZone: 'US/Eastern'
    }
    this.grid.frameworkComponents = { lookupFilter: LookupFilterComponent }
    const columnDefs: ColDef[] = [
      {
        headerName: '',
        field: 'Actions',
        pinned: 'left',
        width: 100,
        cellRenderer: ActionButtonsColumnRendererComponent
      },
      {
        headerName: 'CHI Payer ID',
        field: 'Id',
        width: 75,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Name',
        field: 'Name',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Address',
        field: 'PhysicalAddress.Address1',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'City',
        field: 'PhysicalAddress.CityName',
        width: 100,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'State',
        field: 'PhysicalAddress.StateId',
        width: 60,
        filter: 'lookupFilter',
        cellRenderer: GridWrapperComponent.LookupFieldRenderer(this.states)
      },
      {
        headerName: 'Zip Code',
        field: 'PhysicalAddress.ZipCode',
        width: 60,
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Last Modified By',
        field: 'ModifiedByFullName',
        width: 100,
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Modified On',
        field: 'ModifiedOn',
        width: 100,
        filter: 'agDateColumnFilter',
        cellRenderer: DateTimeColumnRendererComponent,
        filterParams: { newRowsAction: 'keep' }
      }
    ]

    this.grid.gridOptions.columnDefs = columnDefs
    this.grid.defaultExpand = 'PhysicalAddress'
    this.grid.odata = this.odata
  }

  addEntity(): void {
    this.router.navigate(['/system', 'payers', 0])
  }

  editClicked(data: any): void {
    const model = data as IPayer
    this.router.navigate(['/system', 'payers', model.Id])
  }

  deleteClicked(data: any): void {
    this.model = data as IPayer
    this.modalService
      .show(ConfirmModalComponent, {
        initialState: {
          title: `Delete Payer?`,
          message: `Are you sure you want to delete the selected Payer?`
        }
      })
      .content.onClose.subscribe(userConfirm => userConfirm && this.deleteModel())
  }

  deleteModel(): void {
    this.odata
      .delete(this.model.Id)
      .exec()
      .subscribe(response => this.grid.getData())
  }
}
